import React, { useMemo } from "react";
import { useWhatsappAccount } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/WhatsappAccountProvider/useWhatsappAccount";
import { useTranslations } from "@jugl-web/utils";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { WhatsappAccountCard } from "../../../../components/WhatsappAccountCard";
import { CRMConnectionTipsSection } from "../../../../components/CRMConnectionTipsSection/CRMConnectionTipsSection";
import { ReactComponent as AuthenticationIcon } from "./assets/authentication.svg";
import { ReactComponent as BusinessIcon } from "./assets/business.svg";
import { ReactComponent as HealthIcon } from "./assets/health.svg";
import {
  CRMInfoSection,
  CRMInfoSectionItem,
} from "../../../../components/CRMInfoSection";
import { getBusinessVerificationStatusDetails } from "../../../../utils/getBusinessVerificationStatusDetails";
import { getQualityRatingDetails } from "../../../../utils/getQualityRatingDetails";
import { WhatsappAccountMenuTab } from "../../types";

export const WhatsappGeneralInfoTab: React.FC<{
  setSelectedMenuTab: (tab: WhatsappAccountMenuTab) => void;
}> = ({ setSelectedMenuTab }) => {
  const { t } = useTranslations();
  const { entityId } = useEntitySelectedProvider();
  const { whatsappAgent } = useWhatsappAccount();
  const { integrationsApi } = useRestApiProvider();

  const {
    data: whatsappBusinessStatus,
    isLoading: isWhatsappBusinessStatusLoading,
    isFetching: isWhatsappBusinessStatusFetching,
    isError: isWhatsappBusinessStatusError,
    refetch: whatsappBusinessStatusRefetch,
  } = integrationsApi.useGetWhatsappBusinessVerificationStatusQuery({
    entityId,
  });

  const {
    data: whatsappQualityRating,
    isLoading: isWhatsappQualityRatingLoading,
    isFetching: isWhatsappQualityRatingFetching,
    isError: isWhatsappQualityRatingError,
    refetch: whatsappQualityRatingRefetch,
  } = integrationsApi.useGetWhatsappQualityRatingQuery({
    entityId,
  });

  const statusList = useMemo((): CRMInfoSectionItem[] => {
    const list: CRMInfoSectionItem[] = [];

    if (whatsappBusinessStatus) {
      const { status, description } = getBusinessVerificationStatusDetails(
        whatsappBusinessStatus.status,
        t
      );

      list.push({
        title: t({
          id: "client-support-page.business-verification",
          defaultMessage: "Business Verification",
        }),
        onClick: () => {
          setSelectedMenuTab(WhatsappAccountMenuTab.businessVerification);
        },
        status,
        description,
        icon: <BusinessIcon />,
      });
    }

    list.push({
      title: t({
        id: "client-support-page.two-factor-authentication",
        defaultMessage: "Two-factor authentication",
      }),
      description: t({
        id: "client-support-page.two-factor-authentication-description",
        defaultMessage:
          "Two-factor authentication (2FA) is used to protect your WhatsApp Account and make it even more secure with additional authentication step",
      }),
      onClick: () => {
        setSelectedMenuTab(WhatsappAccountMenuTab.twoFactorAuthentication);
      },
      icon: <AuthenticationIcon />,
    });

    if (whatsappQualityRating) {
      const { status, description } = getQualityRatingDetails(
        whatsappQualityRating.rating,
        t
      );

      list.push({
        title: t({
          id: "client-support-page.account-health",
          defaultMessage: "Account Health:",
        }),
        onClick: () => {
          setSelectedMenuTab(WhatsappAccountMenuTab.qualityRating);
        },
        description,
        status,
        icon: <HealthIcon />,
      });
    }

    return list;
  }, [setSelectedMenuTab, t, whatsappBusinessStatus, whatsappQualityRating]);

  if (!whatsappAgent) return null;
  return (
    <>
      <WhatsappAccountCard />
      {whatsappAgent.status === "active" ? (
        <CRMInfoSection
          dataLoadingWrapper={{
            isLoading:
              isWhatsappBusinessStatusLoading ||
              isWhatsappBusinessStatusFetching ||
              isWhatsappQualityRatingLoading ||
              isWhatsappQualityRatingFetching,
            isError:
              isWhatsappBusinessStatusError || isWhatsappQualityRatingError,
            onRetry: () => {
              if (isWhatsappBusinessStatusError) {
                whatsappBusinessStatusRefetch();
              }
              if (isWhatsappQualityRatingError) {
                whatsappQualityRatingRefetch();
              }
            },
          }}
          title={t({
            id: "client-support-page.account-info",
            defaultMessage: "Account info:",
          })}
          subtitle={t({
            id: "client-support-page.take-care-following-items",
            defaultMessage:
              "Take care of following items to make your Account even better",
          })}
          items={statusList}
        />
      ) : (
        <CRMConnectionTipsSection />
      )}
    </>
  );
};
